/**
 * /drafts — all of the current user's drafts. Inline delete; clicking a row
 * opens the editor.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/apiClient.ts';
import { MeetingList } from './DashboardRoute.tsx';
import type { MeetingListItem } from '../components/MeetingRow.tsx';

export function DraftsRoute() {
  const myDrafts = useQuery({
    queryKey: ['meetings', 'mine', 'drafts', 'all'],
    queryFn: () =>
      api.get<{ meetings: MeetingListItem[] }>('/meetings', {
        scope: 'mine',
        status: 'draft',
        limit: 100,
      }),
  });

  return (
    // Mockup .shell + drafts-header layout: max-w 1040, header
    // padding 32/0/12, h1 36px Aeonik 400, subtitle 15px charcoal/78,
    // pine pulse CTA on the right.
    <div className="mx-auto max-w-5xl px-6 pb-16 pt-6">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4 pt-4">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-pine">
            My work
          </div>
          {/* H1 sized to mockup .drafts-header h1: 36px Aeonik 400
              tracking-tight. font-normal (not semibold) keeps the
              page reading as a quiet utility surface. */}
          <h1 className="mt-2 text-[36px] font-normal leading-[1.05] tracking-tight text-charcoal">
            Drafts
          </h1>
          <p className="mt-2 max-w-prose text-[15px] font-light leading-relaxed text-charcoal/78">
            All your meetings that haven&apos;t been finalized yet.
          </p>
        </div>
        {/* Pine pulse pill — same .nm-cta family as NewMeeting.
            Pulse fires twice on first arrival to draw the eye. */}
        <Link
          to="/meetings/new"
          className="motion-safe:animate-pulse-cta inline-flex items-center gap-2 rounded-full border border-charcoal/20 bg-pine px-[18px] py-2.5 text-[13px] font-medium text-cream shadow-md transition-all hover:-translate-y-px hover:bg-pine-deep"
        >
          <span aria-hidden className="text-base leading-none">＋</span>
          New meeting
        </Link>
      </header>

      <MeetingList
        isLoading={myDrafts.isLoading}
        error={myDrafts.error}
        meetings={myDrafts.data?.meetings ?? []}
        emptyMessage="You have no drafts. Create a new meeting to start one."
      />
    </div>
  );
}
